import {
  Box,
  Button,
  Flex,
  FormInput,
  StrokeIcon,
  useIsSticky,
} from '@acsbe/ui';
import { type StripePaymentElementOptions } from '@stripe/stripe-js';
import { type StripeError } from '@stripe/stripe-js/dist/stripe-js/stripe';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { StripeLoader, useStripeModule } from '../../../../../context/stripe';
import { useAnalytics } from '../../../../../services/analytics';
import { PAYMENT_METHOD_REQUESTED } from '../../../../../services/analytics/event-names';
import axios from '../../../../../utils/axiosInstance';
import { useZodForm } from '../../../../../utils/forms';
import { StripeNameCardForm } from '../../../../../validations/billing';

import type { WizardStepComponent } from '../Wizard';

import { useBilling, useMerchantSession } from '../../../../../context';
import { type StripeData } from '../StripeWizard';

export const PaymentDetailsStep: WizardStepComponent<StripeData> = ({
  data,
  setData,
  back,
  next,
  isLast,
}) => {
  const { PaymentElement, useElements, useStripe } = useStripeModule();
  const { hookup, handleSubmit } = useZodForm(StripeNameCardForm);
  const { t } = useTranslation();
  const stripe = useStripe();
  const elements = useElements();
  const analytics = useAnalytics();
  const { isSticking, stickyElementRef } =
    useIsSticky<HTMLDivElement>('bottom');

  const [paymentReady, setPaymentReady] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { setIsNewPaymentMethodAdded } = useBilling();

  const { accessibeUser } = useMerchantSession();

  const paymentElementOptions: StripePaymentElementOptions = {
    terms: {
      card: 'never',
    },
    layout: 'tabs',
    defaultValues: {
      billingDetails: {
        name: data.address?.name,
        address: data.address?.address
          ? {
              line1: data.address.address.line1,
              line2: data.address.address.line2 ?? undefined,
              city: data.address.address.city,
              state: data.address.address.state,
              postal_code: data.address.address.postal_code,
              country: data.address.address.country,
            }
          : undefined,
      },
    },
  } as const;

  const onSubmit = handleSubmit(
    async ({ nameOnCard }: { nameOnCard: string }) => {
      try {
        setIsLoading(true);
        if (!stripe || !elements) {
          return;
        }

        const { error, setupIntent } = await stripe!.confirmSetup({
          elements,
          redirect: 'if_required',
          confirmParams: {
            payment_method_data: {
              billing_details: {
                name: data.address?.name,
                address: data.address?.address
                  ? {
                      line1: data.address.address.line1,
                      line2: data.address.address.line2 ?? undefined,
                      city: data.address.address.city,
                      state: data.address.address.state,
                      postal_code: data.address.address.postal_code,
                      country: data.address.address.country,
                    }
                  : undefined,
              },
            },
          },
        });
        if (error) throw error;

        const response = await axios.post(
          '/api/stripe/add-payment-method',
          {
            setupIntentId: setupIntent.id,
            nameOnCard,
          },
          {
            withCredentials: true,
          },
        );

        setIsNewPaymentMethodAdded(true);
      } catch (err: unknown) {
        const stripeError = err as StripeError;

        analytics.mixpanel.trackEvent(PAYMENT_METHOD_REQUESTED, {
          status: 'fail',
          failureReason:
            stripeError.message?.replace(/['‘’]/g, '') ?? 'unknown',
          isReplaced: false,
        });
      } finally {
        setIsLoading(false);
      }
    },
  );

  if (!stripe) return null;

  return (
    <Flex padding={6} self="stretch">
      <form
        aria-busy={isLoading}
        aria-label="Set up your payment method"
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit();
        }}
      >
        <Flex gap="lg" radius="xl">
          {paymentReady ? (
            <StripeLikeInput
              defaultValue={accessibeUser?.fullName}
              label={t('STRIPE.NAME_LABEL')}
              placeholder={t('STRIPE.NAME_PLACEHOLDER')}
              type="text"
              {...hookup('nameOnCard')}
            />
          ) : (
            <StripeLoader />
          )}

          <Box>
            <PaymentElement
              onReady={() => setPaymentReady(true)}
              options={paymentElementOptions}
            />
          </Box>
        </Flex>

        {paymentReady && (
          <Flex
            data-sticky={isSticking}
            gap={isSticking ? 4 : 6}
            {...(!isSticking && { marginBlockEnd: -2 })}
            marginBlockStart={8}
            position="sticky"
            ref={stickyElementRef}
          >
            <Flex align={'center'} gap={3} justify={'space-between'} row>
              <Button
                aria-busy={isLoading}
                data-mixpanel="add-payment-method-dialog-stripe-setup-payment-method-button"
                disabled={!stripe || !elements || isLoading}
                scale="primary"
                type="submit"
              >
                {t('STRIPE.STRIPE_SETUP_CTA')}
                <StrokeIcon name="ChevronRight" />
              </Button>
            </Flex>
          </Flex>
        )}
      </form>
    </Flex>
  );
};

const StripeLikeInput = styled(FormInput)`
  --xseb-max-input-width: 100%;
  display: flex;

  input {
    flex: 1;
    transition:
      background 0.15s ease,
      border 0.15s ease,
      box-shadow 0.15s ease,
      color 0.15s ease;
  }
`;
